// ============================================================================
// apps/remoteRenderer.tsx — ПРИЛОЖЕНИЕ «Удалённый доступ» (рендерер
// appKind='remote'). Обычный сценарий:
//   • список — ТОЛЬКО пользователи платформы с правами Remote (системные роли
//     remote_client/remote_manage или администратор); список резолвится
//     существующими RPC (auth.getRoles + entity.listEntities по роли + peopleOnly);
//   • клик по сотруднику → запрос согласия (шина remote.session.started.*,
//     принимающий подтверждает во входящем окне RemoteOverlay);
//   • если сотрудник включил вход по паролю (unattended), сначала открывается
//     поле пароля (политика читается через remote.getRemoteAccessSettings —
//     если вызывающему она недоступна, выполняется обычный запрос согласия).
// «Наш контракт» (WebSocket-транспорт) в обычном интерфейсе зрителя НЕ
// выбирается: он остаётся механизмом телефон-хоста (Android) и релея за NAT,
// компоненты RemoteContract* сохранены для этих сценариев.
// Телефон-хост (Android) — вкладка RemoteHostPanel. Возможность для других
// приложений: sdk.apps.invoke('remote','connect',{userId}) — через sdk.expose.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { History, Hash, Loader2, MessageSquare, Monitor, Search, ShieldCheck } from 'lucide-react';
import { TabBar } from '@/components/ui/TabBar';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { registerAppRenderer, type AppSdk, type AppManifest, APPSDK_VERSION } from '@/lib/apps/sdk';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { createLogger } from '@/lib/logger';
import { friendlyError, isPermissionDeniedError } from '@/lib/errors';
import { logEvent } from '@/lib/eventLog';
import { openFedChatByHydraId } from '@/lib/fedChat';
import { HydraIdSearch, type HydraIdPerson } from '@/components/hydra/HydraIdSearch';
import {
  REMOTE_CLIENT_ROLE,
  REMOTE_MANAGE_ROLE,
  canManageRemote,
  canUseRemote,
  useSecurityAccess,
} from '@/lib/securityRoles';
import { newRemoteSessionId, publishRemoteSignal, remoteTopic, type RemoteRole } from '@/lib/remote/remoteSession';
import { RemotePanel } from '@/components/remote/RemotePanel';
import { RemoteHostPanel } from '@/components/remote/RemoteHostPanel';
import { RemoteAccessSettingsPanel } from '@/components/remote/RemoteAccessSettingsPanel';
import { RemotePasswordConnectDialog } from '@/components/remote/RemotePasswordConnectDialog';
import { isRemoteHostSupported } from '@/lib/remote/remoteHost';

const log = createLogger('RemoteApp');

/** Роли, дающие право на «Удалённый доступ» (SSoT-имена — ядро). Админ включён:
 *  по контракту RemoteService администратор всегда имеет доступ. */
const REMOTE_ROLE_NAMES: string[] = ['admin', REMOTE_CLIENT_ROLE, REMOTE_MANAGE_ROLE];

interface RemoteUser {
  id: string;
  name: string;
  avatarUrl: string;
}

interface ActiveSession {
  sessionId: string;
  role: RemoteRole;
  peerName: string;
  /** Готовые LiveKit-креды (вход по паролю) — иначе RemotePanel берёт их сам. */
  livekit?: { url: string; token: string };
}

/** Часть ответа remote.getRemoteAccessSettings, нужная для выбора сценария. */
interface RemotePolicyInfo {
  effectiveMode?: string;
  passwordSet?: boolean;
}

interface RawEntity {
  id?: string;
  login?: string;
  name?: string;
  avatarUrl?: string;
}

// Ф4: строка истории сеансов («мои сеансы» — host ИЛИ viewer).
interface RemoteSessionRow {
  sessionId: string;
  source: string;
  hostId: string;
  hostName: string;
  viewerId: string;
  viewerName: string;
  status: string;
  startedAt: string;
  durationSec: number;
}

function RemoteApp({ sdk }: { sdk: AppSdk }) {
  const myId = useAuthStore((s) => s.user?.id || '');
  const myName = useAuthStore((s) => s.user?.name || '');
  // РОЛЬ-ГЕЙТ (серверный SSoT — RemoteService): использование Remote доступно
  // админу и ролям «Remote-клиент»/«Remote-управление»; без роли приложение
  // скрыто в доке (useVisibleApps), при прямом открытии — явный отказ ниже.
  const access = useSecurityAccess();
  const allowed = canUseRemote(access);
  const canManage = canManageRemote(access);
  const hostSupported = isRemoteHostSupported();
  const [users, setUsers] = useState<RemoteUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<ActiveSession | null>(null);
  const [tab, setTab] = useState('people');
  const [sessions, setSessions] = useState<RemoteSessionRow[]>([]);
  // Вход по паролю (unattended): выбранный хост-пользователь для диалога пароля.
  const [passwordTarget, setPasswordTarget] = useState<RemoteUser | null>(null);
  // Проверка политики хоста перед подключением (кнопка строки показывает ожидание).
  const [connectingId, setConnectingId] = useState('');
  const [connectError, setConnectError] = useState('');
  // Номер, по которому сейчас открывается чат (кнопка «Написать»): защита от
  // повторного нажатия и понятное состояние ожидания.
  const [writingNumber, setWritingNumber] = useState('');

  // Список сотрудников для подключения — ТОЛЬКО пользователи платформы с
  // правами Remote. Источник — существующие RPC (сервер не меняем):
  //   1) auth.getRoles → id системных ролей remote_client/remote_manage/admin;
  //   2) entity.listEntities({roleId}) по каждой роли → множество id;
  //   3) entity.listEntities({peopleOnly:true}) → люди (без системных учёток/ботов)
  //      и их профиль; пересечение множеств = люди с правами Remote.
  useEffect(() => {
    if (!allowed) return; // без роли список сотрудников не запрашивается
    let cancelled = false;
    setUsersLoading(true);
    setUsersError('');
    (async () => {
      const api = getSDK();
      const [rolesRes, peopleRes] = await Promise.all([
        api.auth.getRoles({}),
        api.entity.listEntities({ peopleOnly: true }),
      ]);
      const roleIds = ((rolesRes?.items || []) as { id?: string; name?: string }[])
        .filter((r) => REMOTE_ROLE_NAMES.includes(String(r.name || '')))
        .map((r) => String(r.id || ''))
        .filter(Boolean);
      if (roleIds.length === 0) return [];
      const byRole = await Promise.all(roleIds.map((roleId) => api.entity.listEntities({ roleId })));
      const remoteIds = new Set<string>();
      for (const list of byRole) {
        for (const e of ((list?.items || []) as RawEntity[])) remoteIds.add(String(e.id || ''));
      }
      const people = (peopleRes?.items || []) as RawEntity[];
      return people
        .filter((p) => {
          const id = String(p.id || '');
          return id !== '' && id !== myId && remoteIds.has(id);
        })
        .map((p) => ({
          id: String(p.id || ''),
          name: p.name || p.login || String(p.id || ''),
          avatarUrl: String(p.avatarUrl || ''),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    })()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setUsers([]);
        setUsersError(friendlyError(e));
        log.error('remote users load failed', { error: String((e as Error)?.message || e) });
        void logEvent({
          level: 'warning',
          category: 'ui',
          message: 'удалённый доступ: не удалось загрузить список сотрудников с правами Remote',
          entityType: 'remote',
          entityCode: 'users',
          details: String((e as Error)?.message || e),
        });
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, myId]);

  // Ф4: история сеансов («мои сеансы») — загрузка при открытии вкладки.
  useEffect(() => {
    if (!allowed || tab !== 'history') return;
    let cancelled = false;
    getSDK().remote.listRemoteSessions({ limit: 50 })
      .then((r: { items?: RemoteSessionRow[] }) => {
        if (!cancelled) setSessions(r?.items || []);
      })
      .catch((e: unknown) => log.error('sessions load failed', { error: String((e as Error)?.message || e) }));
    return () => {
      cancelled = true;
    };
  }, [tab, allowed]);

  /** Запрос согласия: сигнал на показ экрана, принимающий подтверждает сам. */
  const start = useCallback((u: RemoteUser) => {
    const sessionId = newRemoteSessionId('u');
    publishRemoteSignal(remoteTopic.started(sessionId), { user_id: myId, name: myName, invite: [u.id] });
    void logEvent({
      level: 'info',
      category: 'ui',
      message: 'удалённый доступ: запрос отправлен',
      entityType: 'remote',
      entityCode: 'session',
      objectId: sessionId,
      details: JSON.stringify({ to: u.id, toName: u.name }),
    });
    setActive({ sessionId, role: 'viewer', peerName: u.name });
  }, [myId, myName]);

  // Клик по сотруднику: сначала узнаём политику хоста.
  //   • включён вход по паролю (mode=password + пароль задан) → поле пароля;
  //   • иначе (или политика недоступна вызывающему — нужен remote_manage) →
  //     обычный запрос согласия; это не ошибка, а разграничение доступа.
  const connectTo = useCallback(async (u: RemoteUser) => {
    if (connectingId) return;
    setConnectError('');
    setConnectingId(u.id);
    try {
      const policy = await getSDK().remote.getRemoteAccessSettings({ hostId: u.id }) as RemotePolicyInfo;
      if (policy?.effectiveMode === 'password' && policy?.passwordSet === true) {
        setPasswordTarget(u);
        return;
      }
      start(u);
    } catch (e: unknown) {
      if (isPermissionDeniedError(e)) {
        // Политику чужого хоста видит только «Remote-управление» — для
        // остальных доступен обычный запрос согласия (сервер его обработает).
        start(u);
        return;
      }
      setConnectError(friendlyError(e));
      log.error('remote access policy failed', { error: String((e as Error)?.message || e), hostId: u.id });
    } finally {
      setConnectingId('');
    }
  }, [connectingId, start]);

  // Возможность приложения: подключиться к сотруднику (для чата/карточек и др.)
  useEffect(() => {
    if (!allowed || !sdk?.expose) return;
    sdk.expose.registerAction('remote', {
      code: 'connect',
      name: 'Удалённый доступ',
      handler: async (payload) => {
        const userId = String((payload as { userId?: string } | null)?.userId || '');
        if (!userId) throw new Error('remote.connect: нужен {userId}');
        const known = users.find((x) => x.id === userId);
        await connectTo({ id: userId, name: known?.name || userId, avatarUrl: known?.avatarUrl || '' });
      },
    });
  }, [sdk, users, connectTo, allowed]);

  // «Написать» из результатов поиска по номеру HydraID: личный чат по номеру
  // (SSoT — сервер: номер этой базы → обычный чат, номер другой базы →
  // межбазовый через Хаб). Открывается диалог, который вернул сервер.
  const { showError } = useToastStore();
  const writeTo = useCallback(async (p: HydraIdPerson) => {
    if (writingNumber) return;
    setWritingNumber(p.number);
    try {
      await openFedChatByHydraId(p.number);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Не удалось открыть чат');
    } finally {
      setWritingNumber('');
    }
  }, [showError, writingNumber]);

  // Кнопка «Написать»: у ЛОКАЛЬНОЙ карточки — как раньше (личный чат, в том
  // числе в другой базе); у СЕТЕВОЙ (remote) — единственное действие: локального
  // пользователя чужой базы нет (недоступны ни показ экрана, ни подключение по паролю).
  const writeButton = (p: HydraIdPerson) => (
    <button
      type="button"
      title={p.remote
        ? `Написать в личном чате (другая база: ${p.baseSlug || '—'})`
        : 'Написать в личном чате (в том числе в другой базе)'}
      disabled={writingNumber !== ''}
      onClick={() => void writeTo(p)}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-1.5 text-[11px] text-muted-foreground hover:bg-muted/60 hover:text-primary disabled:opacity-50"
    >
      <MessageSquare className="h-3.5 w-3.5" />
      {writingNumber === p.number ? 'Открываю…' : 'Написать'}
    </button>
  );

  const filtered = useMemo(
    () => users.filter((u) => u.id !== myId && (!search || u.name.toLowerCase().includes(search.toLowerCase()))),
    [users, myId, search],
  );
  // Быстрая проверка «сотрудник с правами Remote» для действий поиска по номеру.
  const remoteIds = useMemo(() => new Set(users.map((u) => u.id)), [users]);

  // ЯВНЫЙ ОТКАЗ без роли (приложение доступно по прямой ссылке, но не по праву).
  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md rounded-lg border bg-card p-5 text-center">
          <ShieldCheck className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <div className="text-sm font-medium">Нет доступа к «Удалённому доступу»</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Требуется системная роль «Remote-клиент» (remote_client) или
            «Remote-управление» (remote_manage), либо права администратора.
            Роль назначает администратор базы.
          </div>
        </div>
      </div>
    );
  }

  if (active) {
    return (
      <RemotePanel
        sessionId={active.sessionId}
        role={active.role}
        hostName={active.peerName}
        livekit={active.livekit}
        onLeave={() => setActive(null)}
      />
    );
  }

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <Monitor className="h-5 w-5 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Удалённый доступ</div>
          <div className="text-[11px] text-muted-foreground">
            Нажмите на сотрудника — отправится запрос на показ экрана. Если сотрудник настроил пароль, откроется поле пароля.
          </div>
        </div>
      </div>

      <div className="border-b px-4 py-2">
        <TabBar
          tabs={[
            { id: 'people', label: 'Подключение' },
            { id: 'history', label: 'История' },
            ...(hostSupported ? [{ id: 'host', label: 'Телефон-хост' }] : []),
            ...(canManage ? [{ id: 'settings', label: 'Настройки' }] : []),
          ]}
          active={tab}
          onChange={setTab}
          group="remote-app"
        />
      </div>

      {tab === 'host' ? (
        <div className="min-h-0 flex-1">
          <RemoteHostPanel />
        </div>
      ) : canManage && tab === 'settings' ? (
        <div className="min-h-0 flex-1 overflow-auto">
          <RemoteAccessSettingsPanel />
        </div>
      ) : (
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {tab === 'people' && (
          <>
            <div className="px-2 pb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Поиск сотрудника…"
                  className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
            {connectError && (
              <div className="mx-2 mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {connectError}
              </div>
            )}
            {/* Подключение по номеру HydraID (как AnyDesk) — указание владельца
                2026-10-17: поиск человека по номеру живёт ЗДЕСЬ; в профиле его
                нет (там — только свой номер с QR). */}
            <div className="mx-2 mb-3 rounded-lg border p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium">
                <Hash className="h-3.5 w-3.5" /> Подключение по номеру HydraID
              </div>
              <div className="mb-2 text-[10px] leading-snug text-muted-foreground">
                Номер человека (AAA BBB CCC D): найдём и подключимся — как AnyDesk.
                Подключение доступно только людям с правами Remote; номер другой базы
                найдётся в адресной книге сети — с ним доступно «Написать».
              </div>
              <HydraIdSearch
                renderActions={(p) => (p.remote ? (
                  writeButton(p)
                ) : p.entityId === myId ? (
                  <span className="text-[10px] text-muted-foreground">это вы</span>
                ) : (
                  <>
                    {remoteIds.has(p.entityId) ? (
                      <button
                        type="button"
                        disabled={connectingId !== ''}
                        onClick={() => void connectTo({
                          id: p.entityId,
                          name: p.name || p.login || p.display,
                          avatarUrl: p.avatarUrl || '',
                        })}
                        className="shrink-0 rounded-md border px-2.5 py-1.5 text-[11px] text-primary hover:bg-muted/60 disabled:opacity-50"
                      >
                        {connectingId === p.entityId ? 'Подключение…' : 'Подключиться'}
                      </button>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">нет прав Remote</span>
                    )}
                    {writeButton(p)}
                  </>
                ))}
              />
            </div>
            {usersLoading && (
              <div className="flex h-full items-center justify-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Загрузка сотрудников…
              </div>
            )}
            {!usersLoading && usersError && (
              <div className="mx-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {usersError}
              </div>
            )}
            {!usersLoading && !usersError && filtered.length === 0 && (
              <div className="flex h-full items-center justify-center px-6 text-center text-xs italic text-muted-foreground">
                {users.length === 0
                  ? 'Нет сотрудников с правами Remote. Назначьте роль «Remote-клиент» или «Remote-управление» в карточке пользователя.'
                  : 'Сотрудники не найдены'}
              </div>
            )}
            {filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                disabled={connectingId !== ''}
                onClick={() => void connectTo(u)}
                className="flex w-full min-w-0 items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-muted/60 disabled:opacity-60"
              >
                <UserAvatar uid={u.id} fileId={u.avatarUrl} name={u.name} size={32} />
                <span className="min-w-0 flex-1 truncate text-sm">{u.name}</span>
                {connectingId === u.id ? (
                  <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Проверяем…
                  </span>
                ) : (
                  <span className="shrink-0 text-[11px] text-primary">Подключиться</span>
                )}
              </button>
            ))}
          </>
        )}

        {tab === 'history' && (
          <>
            {sessions.length === 0 && (
              <div className="flex h-full items-center justify-center text-xs italic text-muted-foreground">
                Сеансов пока нет
              </div>
            )}
            {sessions.map((s) => {
              const iAmHost = s.hostId === myId;
              const peer = iAmHost ? (s.viewerName || 'Гость') : (s.hostName || 'Хост');
              const when = s.startedAt ? new Date(s.startedAt).toLocaleString('ru-RU') : '';
              const dur = s.durationSec > 0
                ? `${Math.floor(s.durationSec / 60)}:${String(s.durationSec % 60).padStart(2, '0')}`
                : '—';
              return (
                <div key={`${s.sessionId}-${s.startedAt}`} className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-muted/40">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <History className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{iAmHost ? 'Показывал экран' : 'Смотрел экран'} · {peer}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {when}{s.source ? ` · ${s.source}` : ''}{s.status === 'ended' ? '' : ' · идёт'}
                    </div>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{dur}</span>
                </div>
              );
            })}
          </>
        )}
      </div>
      )}

      {passwordTarget && (
        <RemotePasswordConnectDialog
          hostId={passwordTarget.id}
          hostName={passwordTarget.name}
          onRequestConsent={() => {
            const target = passwordTarget;
            setPasswordTarget(null);
            if (target) start(target);
          }}
          onConnected={(creds) => {
            const target = passwordTarget;
            setPasswordTarget(null);
            setActive({
              sessionId: creds.sessionId,
              role: 'viewer',
              peerName: target?.name || '',
              livekit: { url: creds.url, token: creds.token },
            });
          }}
          onClose={() => setPasswordTarget(null)}
        />
      )}
    </div>
  );
}

/** Код приложения «Удалённый доступ» (совпадает с kind='app' code='remote'). */
export const REMOTE_APP_CODE = 'remote';

/** Манифест приложения (AppHost): системное, занимает весь воркспейс. */
export const REMOTE_APP_MANIFEST: AppManifest = {
  code: REMOTE_APP_CODE,
  name: 'remote',
  synonym: 'Удалённый доступ',
  appKind: 'remote',
  area: 'full',
  apiVersion: APPSDK_VERSION,
  permissions: ['expose', 'events', 'notify', 'data:read', 'nav'],
};

export function registerRemoteApp(): void {
  registerAppRenderer({
    appKind: 'remote',
    code: REMOTE_APP_CODE,
    title: 'Удалённый доступ',
    Component: RemoteApp,
    manifest: REMOTE_APP_MANIFEST,
  });
}
